import axios from 'axios';
import type {
  FeedbackCategory,
  FeedbackTopic,
  FeedbackReply,
  FeedbackListResponse,
  FeedbackDetailResponse
} from '../types';

const API_BASE_URL = '/feedback';

// 创建带认证的axios实例
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  withCredentials: true,
});

// 请求拦截器 - 添加认证token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 获取反馈分类列表
export const getFeedbackCategories = async (): Promise<FeedbackCategory[]> => {
  const response = await api.get(`${API_BASE_URL}/categories`);
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || '获取分类失败');
};

// 获取反馈主题列表
export const getFeedbackTopics = async (params: {
  page?: number;
  per_page?: number;
  category_id?: number;
  status?: string;
  search?: string;
  sort_by?: string;
}): Promise<FeedbackListResponse> => {
  const response = await api.get(`${API_BASE_URL}/topics`, { params });
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || '获取反馈列表失败');
};

// 创建反馈主题
export const createFeedbackTopic = async (data: {
  title: string;
  content: string;
  category_id: number;
}): Promise<FeedbackTopic> => {
  const response = await api.post(`${API_BASE_URL}/topics`, data);
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || '创建反馈失败');
};

// 获取反馈主题详情
export const getFeedbackTopicDetail = async (topicId: number): Promise<FeedbackDetailResponse> => {
  const response = await api.get(`${API_BASE_URL}/topics/${topicId}`);
  if (response.data.success) {
    return {
      success: true,
      data: response.data.data
    };
  }
  throw new Error(response.data.message || '获取反馈详情失败');
};

// 创建回复
export const createFeedbackReply = async (topicId: number, data: {
  content: string;
  parent_reply_id?: number;
}): Promise<{success: boolean; data?: FeedbackReply; message?: string}> => {
  const response = await api.post(`${API_BASE_URL}/topics/${topicId}/replies`, data);
  if (response.data.success) {
    return {
      success: true,
      data: response.data.data,
      message: response.data.message
    };
  }
  return {
    success: false,
    message: response.data.message || '回复失败'
  };
};

// 更新反馈主题（管理员功能）
export const updateFeedbackTopic = async (topicId: number, data: {
  status?: string;
  is_pinned?: boolean;
  priority?: FeedbackTopic['priority'];
}): Promise<FeedbackTopic> => {
  const response = await api.put(`${API_BASE_URL}/topics/${topicId}`, data);
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || '更新失败');
};

// 删除反馈主题（管理员功能）
export const deleteFeedbackTopic = async (topicId: number): Promise<void> => {
  const response = await api.delete(`${API_BASE_URL}/topics/${topicId}`);
  if (!response.data.success) {
    throw new Error(response.data.message || '删除失败');
  }
};

// 删除回复（管理员功能）
export const deleteFeedbackReply = async (replyId: number): Promise<void> => {
  const response = await api.delete(`${API_BASE_URL}/replies/${replyId}`);
  if (!response.data.success) {
    throw new Error(response.data.message || '删除回复失败');
  }
};
